import Button, { ActionButton } from '@bugbug/core/components/Button';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { DowngradePlanFeedbackDto } from './DowngradePlanModal.schemas';

import type { OrganizationSubscription, PaymentMethod } from '@bugbug/core/types/organizations';
import type { SubscriptionPlan } from '@bugbug/core/types/subscriptions';
import TextArea from '~/components/TextArea';
import useActionState from '~/hooks/useActionState';
import useModal from '~/hooks/useModal';
import { OrganizationActions } from '~/modules/organization/organization.redux';
import {
  selectActivePaymentMethod,
  selectCurrentOrganizationId,
} from '~/modules/organization/organization.selectors';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { subscriptionsApi } from '~/modules/subscriptions/subscriptions.api';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import { DowngradeConfirmedModal } from '../DowngradeConfirmedModal/DowngradeConfirmedModal';
import { getSubscriptionPlanLabel, isLowerPlan } from '../Subscription/Subscription.helpers';
import {
  SubscriptionModalTitle,
  SubscriptionModalContainer,
  SubscriptionModalContent,
  SubscriptionModalDetails,
  SubscriptionModalFooter,
  ErrorMessage,
} from '../Subscription/Subscription.styled';

import { downgradePlanFeedbackSchema } from './DowngradePlanModal.schemas';
import * as S from './DowngradePlanModal.styled';

import QuestionMarkIcon from '~/images/question-mark-icon.svg?react';

interface DowngradePlanModalProps {
  activeSubscription: OrganizationSubscription;
  downgradePlan: SubscriptionPlan;
  downgradeParallels: number;
}

export const DowngradePlanModal = ({
  activeSubscription,
  downgradePlan,
  downgradeParallels,
}: DowngradePlanModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'downgradeToLowerPlanModal' });
  const [error, setError] = useState<string | null>(null);
  const modal = useModal();
  const activePaymentMethod = useAppSelector(selectActivePaymentMethod) as PaymentMethod;
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const dispatch = useAppDispatch();

  const showBillingCycle = activeSubscription.billingCycle !== downgradePlan.billingCycle;
  const activeSubscriptionParallelValue =
    activeSubscription.addons.find((addon) => addon.type === 'extra_parallel_runs_limit')?.value ??
    0;

  const onSubmit = (values: DowngradePlanFeedbackDto) => {
    const parallelAddon = downgradePlan.possibleAddons.find(
      (addon) => addon.type === 'extra_parallel_runs_limit' && addon.value === downgradeParallels,
    );

    dispatch(
      OrganizationActions.subscribeRequest({
        price: downgradePlan.priceNickname,
        addons: parallelAddon
          ? [{ price: parallelAddon.priceNickname, quantity: parallelAddon.value }]
          : [],
        paymentMethodId: activePaymentMethod.id,
        coupon: null,
        feedback: {
          ...values,
          reasons: [],
        },
      }),
    );
  };

  const formik = useFormik<DowngradePlanFeedbackDto>({
    initialValues: {
      other: '',
    },
    validationSchema: downgradePlanFeedbackSchema,
    onSubmit,
  });

  const handleSubmitSuccess = () => {
    dispatch(subscriptionsApi.util.invalidateTags(['subscriptions']));
    modal.showCustom(DowngradeConfirmedModal);
  };

  const handleSubmitError = (requestErrors: Record<string, string>) => {
    setError(
      Object.values(requestErrors)[0] ??
        t('unexpectedServerError', 'Unexpected server error. Try again.'),
    );
  };
  const downgradeRequestState = useActionState(OrganizationActions.subscribeRequest, {
    onFailure: handleSubmitError,
    onSuccess: handleSubmitSuccess,
  });

  const cancelDowngrade = () => {
    analytics.trackEvent('cancel_downgrade_button_clicked', {
      [TRACK_EVENT_ARG_TYPE.BUTTON_TYPE]: 'changed_my_mind',
      [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
      [TRACK_EVENT_ARG_TYPE.ACCOUNT_PLAN]: activeSubscription.type,
      [TRACK_EVENT_ARG_TYPE.PLAN_DOWNGRADED_TO]: downgradePlan.type,
      [TRACK_EVENT_ARG_TYPE.DOWNGRADE_TYPE]: isLowerPlan(downgradePlan, activeSubscription)
        ? 'plan_downgrade'
        : 'parallel_runs_downgrade',
    });
    modal.hide();
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <SubscriptionModalContainer>
        <SubscriptionModalContent>
          <QuestionMarkIcon width={48} height={48} />
          <SubscriptionModalDetails>
            <SubscriptionModalTitle>
              {t('title', 'Are you sure you want to downgrade?')}
            </SubscriptionModalTitle>
            <div>
              <Trans i18nKey="description">
                <S.Description>
                  Your current plan is{' '}
                  <S.PlanName>
                    {getSubscriptionPlanLabel({
                      plan: activeSubscription,
                      parallels: activeSubscriptionParallelValue,
                      includeBillingCycle: showBillingCycle,
                    })}
                  </S.PlanName>
                  .
                </S.Description>
                <S.Description>
                  You are downgrading it to{' '}
                  <S.PlanName>
                    {getSubscriptionPlanLabel({
                      plan: downgradePlan,
                      parallels: downgradeParallels,
                      includeBillingCycle: showBillingCycle,
                    })}
                  </S.PlanName>
                  .
                </S.Description>
              </Trans>
            </div>
            <div>
              <Trans i18nKey="feedbackInfo">
                <S.Description>
                  Are you sure? Could you tell us why do you want to change it?
                </S.Description>
                <S.Description> We read every answer!</S.Description>
              </Trans>
            </div>
            <TextArea
              {...formik.getFieldProps('other')}
              {...formik.getFieldMeta('other')}
              autoSize
              maxInitialHeight={220}
              onChange={formik.handleChange}
              initialHeight={87}
              placeholder={t('feedbackPlaceholder', 'Add your feedback...')}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </SubscriptionModalDetails>
        </SubscriptionModalContent>
        <SubscriptionModalFooter>
          <Button
            variant="primary"
            bordered
            onClick={cancelDowngrade}
            disabled={downgradeRequestState.isLoading}
          >
            {t('cancel', 'I changed my mind, keep my plan')}
          </Button>
          <ActionButton
            type="submit"
            bordered
            variant="danger"
            disabled={downgradeRequestState.isLoading || !!formik.errors.other}
            pending={downgradeRequestState.isLoading}
          >
            {t('confirmDowngrade', 'Confirm downgrade')}
          </ActionButton>
        </SubscriptionModalFooter>
      </SubscriptionModalContainer>
    </form>
  );
};
